import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Theme } from '../../theme';

const FloatingButton = styled.button<{ $visible: boolean }>`
  position: fixed;
  bottom: 80px; /* Above the bottom nav */
  right: 16px;
  width: 48px;
  height: 48px;
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 50%;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  z-index: 999;
  
  /* Smooth show/hide animation */
  opacity: ${props => props.$visible ? 1 : 0};
  transform: ${props => props.$visible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.8)'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
  
  /* Shadow for depth */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  
  /* Hover effect */
  &:hover {
    background: ${props => props.theme.primary}dd;
    transform: ${props => props.$visible ? 'translateY(-2px) scale(1.05)' : 'translateY(20px) scale(0.8)'};
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
  
  /* Active effect */
  &:active {
    transform: ${props => props.$visible ? 'translateY(0) scale(0.95)' : 'translateY(20px) scale(0.8)'};
  }
  
  /* Prevent text selection */
  -webkit-user-select: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  
  /* Safe area support for iPhone notch */
  bottom: max(80px, calc(80px + env(safe-area-inset-bottom)));
`;

interface BackToTopButtonProps {
  theme: Theme;
  containerRef: React.RefObject<HTMLDivElement>;
  scrollThreshold?: number;
}

const BackToTopButton: React.FC<BackToTopButtonProps> = ({
  theme,
  containerRef,
  scrollThreshold = 200
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      setIsVisible(scrollTop > scrollThreshold);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [scrollThreshold, containerRef]);

  const scrollToTop = () => {
    const container = containerRef.current;
    if (!container) return;

    container.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <FloatingButton
      $visible={isVisible}
      theme={theme}
      onClick={scrollToTop}
      title="Back to top"
      aria-label="Back to top"
    >
      ↑
    </FloatingButton>
  );
};

export default BackToTopButton;
